import { CSSProperties, useCallback, useEffect, useMemo, useRef } from "react";
import { Cell, Row } from "react-aria-components";
import { ApolloQueryResult } from "@apollo/client";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import useTableSort from "components/Table/useTableSort";
import useTitle from "hooks/useTitle";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored, InfoCircle } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useSelectionStore from "components/Table/useSelectionStore";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import Button from "ds/components/Button";
import { FILTERS_ORDER_SETTINGS_KEY as FILTERS_ORDER_SETTINGS_KEY_IDP_GROUP_ROLES } from "views/Account/Settings/IdpGroupMappingV2/constants";
import useTypedContext from "hooks/useTypedContext";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";

import RolesListItem from "./ListItem";
import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROW_HEADER_ID,
  SORT_CONFIG,
  IDP_GROUP_ROLES_COLUMN_CONFIG,
  IDP_GROUP_ROLES_COLUMNS_STORAGE_KEY,
  IDP_GROUP_ROLES_CUSTOM_END_COLUMN_CONFIG,
} from "./constants";
import { RoleBinding } from "./types";
import { useFilteredEntities } from "./useFilteredEntities";
import IdpGroupRolesEmpty from "./Empty";
import IdpGroupRolesBulkActions from "./BulkActions";
import { GetUserData } from "../gql";
import useIdpGroupRolesListAnalytics from "./useIdpGroupRolesListAnalytics";
import { showRolesInfoDrawer } from "../../Roles/InfoDrawer";
import { showAssignRoleDrawer } from "../AssignRoleDrawer";
import { SettingsContext } from "../../Context";

type IdpGroupRolesProps = {
  roleBindings: RoleBinding[];
  groupName: string;
  idpGroupId: string;
  refetch: () => Promise<ApolloQueryResult<GetUserData>>;
};

const IdpGroupRoles = ({ roleBindings, groupName, refetch, idpGroupId }: IdpGroupRolesProps) => {
  const { resetAllSelected, selected, unselect, resetStore } = useSelectionStore();
  const { activationStatus } = useTypedContext(SettingsContext);

  // reset store when unmounted
  useEffect(() => resetStore, [resetStore]);

  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const trackSegmentAnalyticsEvent = useIdpGroupRolesListAnalytics();

  const filteredRoles = useFilteredEntities(roleBindings, sortDescriptor);

  const isPageEmpty = filteredRoles.length === 0;

  useTitle(`Organization Settings · IdP Groups · ${groupName} · Roles`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "Idp Groups",
        link: getBreadcrumbsBackUrl(
          "/settings/idp-group-mapping",
          FILTERS_ORDER_SETTINGS_KEY_IDP_GROUP_ROLES
        ),
      },
      {
        title: groupName,
      },
    ],
    []
  );

  const handleBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const onBulkResetAll = useCallback(() => {
    resetAllSelected();
  }, [resetAllSelected]);

  const openInfoDrawer = () => {
    trackSegmentAnalyticsEvent("Info Click");
    showRolesInfoDrawer();
  };

  const hasOnlyOneRole = useMemo(() => {
    return roleBindings.length === 1;
  }, [roleBindings]);

  const renderRow = useCallback(
    (
      item: Partial<RoleBinding> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return (
          <Row>
            <Cell style={{ height: item.height }} />
          </Row>
        );
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <RolesListItem roleBinding={item as RoleBinding} isLastRole={hasOnlyOneRole} />
        </TableRow>
      );
    },
    [hasOnlyOneRole]
  );

  const memoizedSelectedIdpGroupRolesMap = useMemo(
    () => new Map(roleBindings.map((role) => [role.id, role])),
    [roleBindings]
  );

  return (
    <>
      <PageInfoHeaderWrapper>
        <Box direction="row" align="center" gap="medium">
          <PageInfoHeaderTitle title="Roles" />
          <ButtonIcon onPress={openInfoDrawer} icon={InfoCircle} variant="ghost">
            Open info drawer
          </ButtonIcon>
        </Box>
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search..."
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
          />

          <Button
            variant="primary"
            analyticsPage={AnalyticsPageOrganization.OrganizationIdpGroupRoles}
            analyticsTitle="Manage Roles Clicked"
            onPress={() => showAssignRoleDrawer({ userGroupId: idpGroupId, activationStatus })}
          >
            Manage roles
          </Button>
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <TableContextProvider
        selectable
        columnsConfig={IDP_GROUP_ROLES_COLUMN_CONFIG}
        endCustomColumns={IDP_GROUP_ROLES_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={IDP_GROUP_ROLES_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
          {!!filteredRoles.length && (
            <Table ariaLabel="Roles" items={filteredRoles} ref={virtualizedListContainerRef}>
              {renderRow}
            </Table>
          )}

          {selected.size > 0 && (
            <IdpGroupRolesBulkActions
              virtualizedListContainerRef={virtualizedListContainerRef}
              selectedSet={selected}
              idpGroupRolesMap={memoizedSelectedIdpGroupRolesMap}
              onBulkResetAll={onBulkResetAll}
              onItemDismiss={unselect}
              onFinish={handleBulkActionsFinish}
            />
          )}
          {!roleBindings?.length && <IdpGroupRolesEmpty />}
          {isPageEmpty && !!roleBindings?.length && (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          )}
        </TableWrapper>
      </TableContextProvider>
    </>
  );
};

export default IdpGroupRoles;
